import React from 'react'

// components
import { IMAGE_PATHS_V2 } from '@/shared/consts/imagesPaths'
import { ModalPage } from '@/components/ui/modals/ModalPage.component'
import { ImageResponsive } from '@/components/commons/images/ImageResponsive.component'
import { Subtitle } from '@/components/commons/typography/Subtitle'
import { Text } from '@/components/commons/typography/Text'
import { RenderButton } from './ButtonConnection'
import { ButtonComponent } from '@/components/commons'
import { connectionCardButton } from '@/components/connectionCard/ConnectionCard.css'
import { OauthRequestSocialType } from 'containers/oAuth/types/oAuth'

interface GoogleModalConnectionProps {
  modalOpen: boolean
  onClose: () => void
  handleClick: () => void
  social: OauthRequestSocialType
  loading?: boolean
}

export const GoogleModalConnection: React.FC<GoogleModalConnectionProps> = ({
  modalOpen,
  onClose,
  handleClick,
  social,
  loading,
}) => {
  const [readMoreActive, setReadMoreActive] = React.useState<boolean>(false)

  return (
    <ModalPage show={modalOpen} className={['p-40 full-w']} onClose={onClose}>
      <div className="flex flex-column gap-8">
        <ImageResponsive
          src={IMAGE_PATHS_V2.analytics + 'google_connection_creatorland.png'}
          alt="google connection creatorland"
        />
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ margin: '0' }}>Syncing Your Google Account with Creatorland</h2>
        </div>

        {!readMoreActive ? (
          <>
            <Text>
              By syncing your Google account, you’re unlocking Creatorland’s AI-powered VIP networking tools:
            </Text>
            <ul style={{ paddingLeft: '15px', color: '#444444' }}>
              <li>
                <span style={{ fontWeight: 500 }}>Gmail:</span> Identify potential deals and past collaborations from your inbox
              </li>
              <li>
                <span style={{ fontWeight: 500 }}>Google Contacts:</span> Discover creators and brands you’ve already connected with
              </li>
              <li>
                <span style={{ fontWeight: 500 }}>YouTube Analytics:</span> Access performance insights to attract better brand deals
              </li>
            </ul>
            <Text weightClass='font-medium' >
              Your privacy is our priority:
            </Text>
            <ul style={{ paddingLeft: '15px', color: '#444444' }}>
              <li>
                We never store, transfer, or sell your email data.
              </li>
              <li>
                You control who you connect with and manage your contacts anytime
              </li>
              <li>
                All of Creatorland’s policies and features confirm with the <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  className="text-info"
                  target="_BLANK"
                  rel="noopener noreferrer"
                >Google API Terms of Service</a>.
              </li>
            </ul>

            <Text>
              For more information, please view our <a
                href="https://www.creatorland.com/privacy"
                className="text-info"
                target="_BLANK"
                rel="noopener noreferrer"
              >Privacy Policy</a> & <a
                href="https://www.creatorland.com/terms"
                className="text-info"
                target="_BLANK"
                rel="noopener noreferrer"
              >Terms of Service</a> or reach out to the Creatorland team at support@creatorland.com.
            </Text>
          </>
        ) : (
          <>
            <Text>
              By connecting your Google account, Creatorland helps you make smarter connections by analyzing relevant signals across your:
            </Text>
            <div>
              <ul style={{ paddingLeft: '15px', color: '#444444' }}>
                <li>
                  <span style={{ fontWeight: 500 }}>Gmail:</span> Identify VIP contacts from past brand deals and collaborations in your inbox
                </li>
                <li>
                  <span style={{ fontWeight: 500 }}>Google Contacts:</span> Discover creators and industry professionals already in your network
                </li>
                <li>
                  <span style={{ fontWeight: 500 }}>YouTube Analytics:</span> Gain insights into how your content is performing to optimize future collaborations
                </li>
              </ul>
            </div>
            <Text>
              These insights help you reconnect with brands, build stronger relationships, and discover new opportunities seamlessly.
            </Text>
            <Text>
              We take privacy seriously: Your email contents are never stored, transferred, or shared. We only use metadata to enhance your networking experience. You can revoke access at any time.
            </Text>
          </>
        )}

        <RenderButton
          className="mt-24"
          handleClick={handleClick}
          social={social}
          loading={loading}
          avatarSize={48}
          isInModal
          label="Continue with Gmail"
        />

        {!readMoreActive ? (
          <div
            onClick={() => {
              setReadMoreActive(true)
            }}
            style={{ textAlign: 'center', marginTop: '10px' }}
          >
            <span
              className="mt-8 cursor-pointer"
              style={{ color: '#949494' }}
            >
              Learn more
            </span>
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <Text style={{ color: '#949494' }}>
              <a
                href="https://developers.google.com/terms"
                className="text-info"
                target="_BLANK"
                rel="noopener noreferrer"
                style={{ color: '#949494' }}
              >Google API Terms of Service</a> | <a
                href="https://www.creatorland.com/privacy"
                className="text-info"
                target="_BLANK"
                rel="noopener noreferrer"
                style={{ color: '#949494' }}
              >Privacy Policy</a>
            </Text>
            <div
              onClick={() => {
                setReadMoreActive(false)
              }}
              style={{ textAlign: 'center', marginTop: '10px' }}
            >
              <span
                className="mt-8 cursor-pointer"
                style={{ color: '#949494' }}
              >
                Not Right Now
              </span>
            </div>
          </div>
        )}

      </div>
    </ModalPage>
  )
}

export const SocialModalDisconnect: React.FC<GoogleModalConnectionProps> = ({
  modalOpen,
  onClose,
  handleClick,
}) => {
  return (
    <ModalPage show={modalOpen} className={['p-40 full-w']} onClose={onClose}>
      <div className="flex flex-column gap-8 mt-24">
        <Subtitle>Are you sure you want to disconnect this account?</Subtitle>

        <div className="flex justify-center">
          <ButtonComponent
            className={`mt-24 mr-24 ${connectionCardButton}`}
            size="small"
            label="No"
            variation="secondary"
            onClick={onClose}
          />
          <ButtonComponent
            className={`mt-24 mr-24 ${connectionCardButton}`}
            size="small"
            label="Yes"
            variation="primary"
            onClick={handleClick}
          />
        </div>
      </div>
    </ModalPage>
  )
}
